import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, ScrollRestoration, useLocation, useRouteError } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AppLayout } from './components/AppLayout';
import { DesktopLayout } from './components/DesktopLayout';
import { SwipeBack } from './components/SwipeBack';
import { NavigationProvider, useNavigation } from './components/NavigationContext';
import { ToastContainer } from './components/Toast';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';
import { OnboardingGuard } from './components/OnboardingGuard';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useSessionStore } from './stores/session-store';
import { useLocaleStore } from './stores/locale-store';
import { t } from './i18n/locale';
import { useIsDesktop } from './hooks/useMediaQuery';
import styles from './App.module.css';

const ROUTE_IMPORT_RELOAD_KEY = 'aeterna:route-import-error-reloaded';

/* ─────────────────────────────────────────────────────
   路由级懒加载 (React.lazy)
   · 5 个 Tab 页面 → 同步加载（首屏必须立即可见）
   · 其余全部页面 → 懒加载（按需加载各功能域 chunk）
   ───────────────────────────────────────────────────── */

/* ── Tab Pages (同步 — 首屏) ── */
import { HealthDashboardPage } from './pages/tabs/HealthDashboardPage';
import HealthKnowledgePage from './pages/tabs/HealthKnowledgePage';
import CommunityPage from './pages/tabs/CommunityPage';
import { AiConsultantPage } from './pages/tabs/AiConsultantPage';
import LongevityHomePage from './pages/execute/LongevityHomePage';


/* ── Auth (懒加载) ── */
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const InvitePage = lazy(() => import('./pages/auth/InvitePage').then(m => ({ default: m.InvitePage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const BindPage = lazy(() => import('./pages/auth/BindPage').then(m => ({ default: m.BindPage })));

/* ── Landing Page (懒加载 — Web 桌面端品牌首页) ── */
const LandingPage = lazy(() => import('./pages/landing/LandingPage').then(m => ({ default: m.LandingPage })));

/* ── Splash & Onboarding (懒加载) ── */
const BrandSplashScreen = lazy(() => import('./pages/BrandSplashScreen').then(m => ({ default: m.BrandSplashScreen })));
const OnboardingPage = lazy(() => import('./pages/onboarding/Onboarding').then(m => ({ default: m.OnboardingPage })));
const NotFitPage = lazy(() => import('./pages/onboarding/NotFit').then(m => ({ default: m.NotFitPage })));

/* ── Illuminate (懒加载) ── */
const MetricDetailPage = lazy(() => import('./pages/illuminate/MetricDetailPage').then(m => ({ default: m.MetricDetailPage })));
const HealthDimensionMatrixPage = lazy(() => import('./pages/illuminate/HealthDimensionMatrixPage').then(m => ({ default: m.HealthDimensionMatrixPage })));
const MedicalReportTimelinePage = lazy(() => import('./pages/illuminate/MedicalReportTimelinePage').then(m => ({ default: m.MedicalReportTimelinePage })));
const ReportDetailPage = lazy(() => import('./pages/illuminate/ReportDetailPage'));
const BiologicalAgePage = lazy(() => import('./pages/illuminate/BiologicalAgePage').then(m => ({ default: m.default })));

/* ── Ask (懒加载) ── */
const QuestionHubPage = lazy(() => import('./pages/ask/QuestionHubPage').then(m => ({ default: m.QuestionHubPage })));
const QuestionDetailPage = lazy(() => import('./pages/ask/QuestionDetailPage').then(m => ({ default: m.QuestionDetailPage })));
const AskQuestionFormPage = lazy(() => import('./pages/ask/AskQuestionFormPage').then(m => ({ default: m.AskQuestionFormPage })));
const ChatSessionListPage = lazy(() => import('./pages/ask/ChatSessionListPage').then(m => ({ default: m.ChatSessionListPage })));

/* ── Assess (懒加载) ── */
const AssessmentHubPage = lazy(() => import('./pages/assess/AssessmentHubPage'));
const AssessmentFormPage = lazy(() => import('./pages/assess/AssessmentFormPage'));
const AssessmentResultPage = lazy(() => import('./pages/assess/AssessmentResultPage'));
const NeuroTestPage = lazy(() => import('./pages/assess/NeuroTestPage').then(m => ({ default: m.NeuroTestPage })));

/* ── Execute (懒加载) ── */
const InterventionHubPage = lazy(() => import('./pages/execute/InterventionHubPage'));
const InterventionIntroPage = lazy(() => import('./pages/execute/InterventionIntroPage'));
const LongevityCreatePage = lazy(() => import('./pages/execute/LongevityCreatePage'));
const LongevityDayPage = lazy(() => import('./pages/execute/LongevityDayPage'));
const LongevityGoalWallPage = lazy(() => import('./pages/execute/LongevityGoalWallPage'));
const LongevityHistoryPage = lazy(() => import('./pages/execute/LongevityHistoryPage'));
const LongevityWorkoutDetailPage = lazy(() => import('./pages/execute/LongevityWorkoutDetailPage'));
const DoctorVisitPrepPage = lazy(() => import('./pages/execute/DoctorVisitPrepPage'));
const VisualCardGalleryPage = lazy(() => import('./pages/execute/VisualCardGalleryPage'));
const RecipeDetailPage = lazy(() => import('./pages/execute/RecipeDetailPage'));
const RecipeHubPage = lazy(() => import('./pages/execute/RecipeHubPage'));
const PlanDetailPage = lazy(() => import('./pages/execute/PlanDetailPage'));
const ReversalHubPage = lazy(() => import('./pages/execute/ReversalHubPage'));
const ReversalIntroPage = lazy(() => import('./pages/execute/ReversalIntroPage'));

/* ── Knowledge (懒加载) ── */
const KnowledgePodcastSeriesPage = lazy(() => import('./pages/knowledge/KnowledgePodcastSeriesPage'));
const KnowledgePodcastSeriesDetailPage = lazy(() => import('./pages/knowledge/KnowledgePodcastSeriesDetailPage'));
const KnowledgePodcastEpisodePage = lazy(() => import('./pages/knowledge/KnowledgePodcastEpisodePage'));
const KnowledgeVisualCardSetPage = lazy(() => import('./pages/knowledge/KnowledgeVisualCardSetPage'));
const PersonalizedKnowledgeHistoryPage = lazy(() => import('./pages/knowledge/PersonalizedKnowledgeHistoryPage'));

/* ── Profile (懒加载) ── */
const UserProfilePage = lazy(() => import('./pages/profile/UserProfilePage'));
const SettingsPage = lazy(() => import('./pages/profile/SettingsPage'));
const ApiKeysPage = lazy(() => import('./pages/profile/ApiKeysPage'));
const FamilyCarePage = lazy(() => import('./pages/profile/FamilyCarePage'));
const FamilyComparisonPage = lazy(() => import('./pages/profile/FamilyComparisonPage'));
const HelpFeedbackPage = lazy(() => import('./pages/profile/HelpFeedbackPage'));
const InviteFriendsPage = lazy(() => import('./pages/profile/InviteFriendsPage'));
const MembershipPage = lazy(() => import('./pages/profile/MembershipPage'));
const RedeemMembershipPage = lazy(() => import('./pages/profile/RedeemMembershipPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/profile/IntegrationsSettingsPage'));

/* ── Generosity (懒加载) ── */
const DataDonationPage = lazy(() => import('./pages/generosity/DataDonationPage'));
const BeaconCasesPage = lazy(() => import('./pages/generosity/BeaconCasesPage'));

/* ── Calendar & Commerce (懒加载) ── */
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const AddEventPage = lazy(() => import('./pages/calendar/AddEventPage'));
const CheckoutPage = lazy(() => import('./pages/commerce/CheckoutPage'));
const FavoritesPage = lazy(() => import('./pages/commerce/FavoritesPage'));
const ShopHomePage = lazy(() => import('./pages/commerce/ShopHomePage'));

/* ── Dev (懒加载) ── */
const DevNavPage = lazy(() => import('./pages/dev/DevNavPage'));
const ModelArenaPage = lazy(() => import('./pages/model-arena/ModelArenaPage'));

/* ─── 延迟加载指示器：300ms 内不显示，避免返回导航时闪一下 ─── */
function DelayedFallback() {
  return <LoadingSpinner delay={300} />;
}

function getRouteErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

function isDynamicImportError(message: string) {
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    message,
  );
}

function RouteErrorFallback() {
  const error = useRouteError();
  const locale = useLocaleStore((s) => s.locale);
  const message = getRouteErrorMessage(error);
  const isImportError = isDynamicImportError(message);

  useEffect(() => {
    if (!isImportError) return;
    if (sessionStorage.getItem(ROUTE_IMPORT_RELOAD_KEY)) return;
    sessionStorage.setItem(ROUTE_IMPORT_RELOAD_KEY, '1');
    window.location.reload();
  }, [isImportError]);

  const title = t(locale, isImportError ? 'routeError.import.title' : 'routeError.generic.title');
  const description = t(locale, isImportError ? 'routeError.import.desc' : 'routeError.generic.desc');
  const detail = message || t(locale, 'routeError.unknown');

  return (
    <main className={styles.routeErrorPage}>
      <section className={styles.routeErrorPanel}>
        <p className={styles.routeErrorEyebrow}>AETERNA</p>
        <h1 className={styles.routeErrorTitle}>{title}</h1>
        <p className={styles.routeErrorText}>{description}</p>
        <div className={styles.routeErrorActions}>
          <button
            className={styles.routeErrorPrimary}
            type="button"
            onClick={() => window.location.reload()}
          >
            {t(locale, 'routeError.reload')}
          </button>
          <button
            className={styles.routeErrorSecondary}
            type="button"
            onClick={() => window.location.assign('/illuminate')}
          >
            {t(locale, 'routeError.home')}
          </button>
        </div>
        {!isImportError && (
          <p className={styles.routeErrorDetail}>{detail}</p>
        )}
      </section>
    </main>
  );
}

/* ─── Root Layout: NavigationProvider + MotionConfig + SwipeBack + ScrollRestoration ─── */
function RootLayout() {
  return (
    <NavigationProvider>
      <RootLayoutInner />
    </NavigationProvider>
  );
}

function RootLayoutInner() {
  const { isBack } = useNavigation();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [hidden, setHidden] = useState(false);
  const prevKey = useRef(location.key);

  useEffect(() => {
    sessionStorage.removeItem(ROUTE_IMPORT_RELOAD_KEY);
  }, []);

  // 每次路由变化时：后退 → 先隐藏内容；前进 → 立即显示
  useEffect(() => {
    if (location.key === prevKey.current) return;
    prevKey.current = location.key;

    if (isBack) {
      setHidden(true);
      // 等 ScrollRestoration 恢复位置后再显示
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHidden(false));
      });
    } else {
      setHidden(false);
    }
  }, [location.key, isBack]);

  const content = (
    <div>
      <ScrollRestoration />
      <div style={hidden ? { visibility: 'hidden' } : undefined}>
        <Outlet />
      </div>
      <PwaInstallPrompt />
      <ToastContainer />
    </div>
  );

  return (
    <MotionConfig reducedMotion={isBack ? 'always' : undefined}>
      {isDesktop ? content : <SwipeBack>{content}</SwipeBack>}
    </MotionConfig>
  );
}

/* ─── AuthGuard: 未登录时重定向到 /login (移动端) 或 /landing (桌面端) ─── */
function AuthGuard() {
  const { isAuthenticated, isLoading } = useSessionStore();
  const isDesktop = useIsDesktop();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to={isDesktop ? '/landing' : '/login'} replace />;
  }

  return <Outlet />;
}

function PublicLandingEntry() {
  const { isAuthenticated, isLoading } = useSessionStore();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/illuminate" replace />;
  }

  return <LandingPage />;
}

/* ─── ResponsiveAppLayout: 根据屏幕宽度切换 Mobile TabBar / Desktop Sidebar ─── */
function ResponsiveAppLayout() {
  const isDesktop = useIsDesktop();
  return isDesktop ? <DesktopLayout /> : <AppLayout />;
}

/* ─── 路由表 (createBrowserRouter) ─── */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      /* ── DEV: 路由导航调试页 ── */
      { path: '/dev', element: <DevNavPage /> },
      { path: '/model-arena', element: <ModelArenaPage /> },

      /* ── P0: 认证 + 初见流程 ── */
      { path: '/', element: <PublicLandingEntry /> },
      { path: '/landing', element: <LandingPage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/invite/:code', element: <InvitePage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/auth/bind', element: <BindPage /> },
      { path: '/splash', element: <OnboardingGuard><BrandSplashScreen /></OnboardingGuard> },
      { path: '/onboarding', element: <OnboardingGuard requireAuth><OnboardingPage /></OnboardingGuard> },
      { path: '/onboarding/not-fit', element: <OnboardingGuard><NotFitPage /></OnboardingGuard> },

      /* ── 需要登录的路由 (AuthGuard 保护) ── */
      {
        element: <AuthGuard />,
        children: [
          /* ── P1: 照见子页面 (Illuminate Detail — no tab bar) ── */
          { path: '/illuminate/metric/:metricId', element: <MetricDetailPage /> },
          { path: '/illuminate/dimensions', element: <HealthDimensionMatrixPage /> },
          { path: '/illuminate/reports', element: <MedicalReportTimelinePage /> },
          { path: '/illuminate/report/:reportId', element: <ReportDetailPage /> },
          { path: '/illuminate/biological-age', element: <BiologicalAgePage /> },

          /* ── P2: 解惑子页面 (Ask Detail — no tab bar) ── */
          { path: '/ask/questions', element: <QuestionHubPage /> },
          { path: '/ask/question/:questionId', element: <QuestionDetailPage /> },
          { path: '/ask/new', element: <AskQuestionFormPage /> },
          { path: '/ask/sessions', element: <ChatSessionListPage /> },

          /* ── Assess ── */
          { path: '/assess/hub', element: <AssessmentHubPage /> },
          { path: '/assess/neuro-test', element: <NeuroTestPage /> },
          { path: '/assess/result', element: <AssessmentResultPage /> },
          { path: '/assess/:type', element: <AssessmentFormPage /> },
          { path: '/assess', element: <AssessmentFormPage /> },

          /* ── Execute ── */
          { path: '/execute/interventions', element: <InterventionHubPage /> },
          { path: '/execute/intervention/:id/intro', element: <InterventionIntroPage /> },
          { path: '/execute/longevity/create', element: <LongevityCreatePage /> },
          { path: '/execute/longevity/doctor-prep', element: <DoctorVisitPrepPage /> },
          { path: '/execute/longevity/day/:day/cards', element: <VisualCardGalleryPage /> },
          { path: '/execute/longevity/day/:day', element: <LongevityDayPage /> },
          { path: '/execute/longevity/history', element: <LongevityHistoryPage /> },
          { path: '/execute/longevity/goals', element: <LongevityGoalWallPage /> },
          { path: '/execute/longevity/workouts/:workoutId', element: <LongevityWorkoutDetailPage /> },
          { path: '/execute/recipe/:recipeId', element: <RecipeDetailPage /> },
          { path: '/execute/recipes', element: <RecipeHubPage /> },
          { path: '/execute/plan/:planId', element: <PlanDetailPage /> },
          { path: '/execute/reversal', element: <ReversalHubPage /> },
          { path: '/execute/reversal/:id/intro', element: <ReversalIntroPage /> },

          /* ── Knowledge ── */
          { path: '/knowledge/article/:articleId', element: <Navigate to="/knowledge/podcasts" replace /> },
          { path: '/knowledge/course/:courseId', element: <Navigate to="/knowledge/podcasts" replace /> },
          { path: '/knowledge/experts', element: <Navigate to="/knowledge/podcasts" replace /> },
          { path: '/knowledge/course/:courseId/player', element: <Navigate to="/knowledge/podcasts" replace /> },
          { path: '/knowledge/podcasts', element: <KnowledgePodcastSeriesPage /> },
          { path: '/knowledge/podcasts/:seriesId', element: <KnowledgePodcastSeriesDetailPage /> },
          { path: '/knowledge/podcasts/:seriesId/episodes/:episodeId', element: <KnowledgePodcastEpisodePage /> },
          { path: '/knowledge/podcasts/:seriesId/episodes/:episodeId/cards/:setId', element: <KnowledgeVisualCardSetPage /> },
          { path: '/knowledge/personalized/history', element: <PersonalizedKnowledgeHistoryPage /> },
          { path: '/knowledge/custom-courses', element: <Navigate to="/knowledge/podcasts" replace /> },
          { path: '/knowledge/custom-course/:courseId/lesson/:lessonId', element: <Navigate to="/knowledge" replace /> },
          { path: '/knowledge/custom-course/:courseId/day/:day', element: <Navigate to="/knowledge" replace /> },
          { path: '/knowledge/custom-course/:courseId/day', element: <Navigate to="/knowledge" replace /> },
          { path: '/knowledge/create-course', element: <Navigate to="/execute/longevity/create" replace /> },

          /* ── Profile ── */
          { path: '/profile', element: <UserProfilePage /> },
          { path: '/membership', element: <MembershipPage /> },
          { path: '/membership/redeem', element: <RedeemMembershipPage /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/profile/settings', element: <SettingsPage /> },
          { path: '/profile/api-keys', element: <ApiKeysPage /> },
          { path: '/profile/integrations', element: <IntegrationsSettingsPage /> },
          { path: '/profile/family', element: <FamilyCarePage /> },
          { path: '/profile/family/compare', element: <FamilyComparisonPage /> },
          { path: '/profile/help', element: <HelpFeedbackPage /> },
          { path: '/profile/invite', element: <InviteFriendsPage /> },
          { path: '/profile/pricing', element: <Navigate to="/membership" replace /> },

          /* ── Generosity ── */
          { path: '/generosity/donation', element: <DataDonationPage /> },
          { path: '/generosity/beacon', element: <BeaconCasesPage /> },

          /* ── Calendar & Commerce ── */
          { path: '/calendar', element: <CalendarPage /> },
          { path: '/calendar/add', element: <AddEventPage /> },
          { path: '/commerce/shop', element: <ShopHomePage /> },
          { path: '/checkout', element: <CheckoutPage /> },
          { path: '/favorites', element: <FavoritesPage /> },

          /* ── 主应用 (Main App — Desktop: Sidebar / Mobile: TabBar) ── */
          {
            element: <ResponsiveAppLayout />,
            children: [
              { path: '/illuminate', element: <HealthDashboardPage /> },
              { path: '/execute', element: <Navigate to="/execute/longevity" replace /> },
              { path: '/execute/longevity', element: <LongevityHomePage /> },
              { path: '/knowledge', element: <HealthKnowledgePage /> },
              { path: '/generosity', element: <CommunityPage /> },
              { path: '/ask', element: <AiConsultantPage /> },
            ],
          },
        ],
      },
    ],
  },
]);

/* ─── App 入口 ─── */
export default function App() {
  const bootstrap = useSessionStore((s) => s.bootstrap);
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const locale = useLocaleStore((s) => s.locale);
  const loadLocaleFromProfile = useLocaleStore((s) => s.loadFromProfile);

  // App 挂载时恢复会话
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    if (isAuthenticated) {
      void loadLocaleFromProfile();
    }
  }, [isAuthenticated, loadLocaleFromProfile]);

  return (
    <Suspense fallback={<DelayedFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
